/**
 * The shell's screens, verbatim from the original where the original has
 * them. Labels and nesting are the CD/1996 build's, as captured live and
 * corrected in `docs/game/ui-reference/index.md` and `docs/game/ui-menus.md`:
 * a seven-item Main Menu with `MISSION RECORDER` top-level (not under
 * `OTHER OPTIONS`), and `CHOOSE` as the `OTHER OPTIONS` popup's header.
 *
 * Where the port has a real surface, the original's entry routes to it.
 * Where it doesn't, the entry opens a dialog in the original's own popup
 * style rather than being deleted — the Main Menu stays byte-faithful.
 * `MULTIPLAYER` is this port's one added row and it lives in the popup, not
 * on the board, so the 320×200 frame the player first sees is exactly the
 * 1990 one.
 *
 * `QUIT` is the one item with its own `MenuAction` kind rather than
 * `open`/`navigate`: outside a frame there is somewhere real to go
 * (`shell-runtime.ts`'s `handleQuit` sends the tab to
 * `https://this.os.isfine.org/`, the same idea "close the window" gestures
 * at), so it leaves the game rather than opening `quit-dialog`'s informational
 * text. Embedded in someone else's frame, hijacking the top-level page is not
 * this port's call to make, so `handleQuit` falls back to exactly what QUIT
 * always did — `quit-dialog` stays registered below for that path.
 */

import { buildMenuTree, type MenuScreen, type MenuTree } from "./menu-model.ts";

export const MAIN_MENU_ID = "main";

/** Every route the shell can reach, in one place, so the wiring is greppable. */
export const SHELL_ROUTES = {
  title: "/",
  dogfight: "/fly",
  singleMission: "/missions",
  career: "/career",
  airplanes: "/planes",
  multiplayer: "/duel",
  recorder: "/vcr",
  /**
   * PREFERENCES. The original's entry under `OTHER OPTIONS` configured a sound
   * card, a music device and an input device — questions a browser answers for
   * itself — so the port keeps the *entry* where the capture puts it and gives
   * it the settings this port actually has (`shell/model/preferences-screen.ts`).
   */
  preferences: "/settings",
} as const;

const NOT_IN_PART_ONE = "PART 1 IS THE BASE GAME.";

function dialog(id: string, title: string, body: readonly string[]): MenuScreen {
  return {
    id,
    kind: "popup",
    title,
    body,
    items: [{ id: "continue", label: "CONTINUE", action: { kind: "close" } }],
    cancel: { kind: "close" },
  };
}

const MAIN_MENU: MenuScreen = {
  id: MAIN_MENU_ID,
  kind: "panel",
  title: "MAIN MENU",
  body: [],
  cancel: undefined,
  items: [
    { id: "dogfight-ace", label: "DOGFIGHT A FAMOUS ACE", action: { kind: "navigate", path: SHELL_ROUTES.dogfight } },
    { id: "single-mission", label: "FLY SINGLE MISSION", action: { kind: "navigate", path: SHELL_ROUTES.singleMission } },
    { id: "career", label: "CAREER MENU", action: { kind: "navigate", path: SHELL_ROUTES.career } },
    { id: "airplanes", label: "VIEW AIRPLANES", action: { kind: "navigate", path: SHELL_ROUTES.airplanes } },
    { id: "other-options", label: "OTHER OPTIONS", action: { kind: "open", screen: "other-options" } },
    { id: "recorder", label: "MISSION RECORDER", action: { kind: "navigate", path: SHELL_ROUTES.recorder } },
    { id: "quit", label: "QUIT", action: { kind: "quit" } },
  ],
};

const OTHER_OPTIONS: MenuScreen = {
  id: "other-options",
  kind: "popup",
  title: "CHOOSE",
  body: [],
  cancel: { kind: "close" },
  items: [
    { id: "preferences", label: "PREFERENCES", action: { kind: "navigate", path: SHELL_ROUTES.preferences } },
    { id: "credits", label: "CREDITS", action: { kind: "replayIntro" } },
    { id: "watch-demo", label: "WATCH DEMO", action: { kind: "open", screen: "demo-dialog" } },
    { id: "multiplayer", label: "MULTIPLAYER", action: { kind: "navigate", path: SHELL_ROUTES.multiplayer } },
    { id: "cancel", label: "CANCEL", action: { kind: "close" } },
  ],
};

export const SHELL_SCREENS: readonly MenuScreen[] = [
  MAIN_MENU,
  OTHER_OPTIONS,
  dialog("demo-dialog", "WATCH DEMO", ["THE ATTRACT DEMO IS", "NOT FLYING YET.", NOT_IN_PART_ONE]),
  dialog("quit-dialog", "QUIT", ["THERE IS NO DOS TO RETURN TO.", "CLOSE THE WINDOW TO STOP FLYING."]),
];

export function buildShellTree(): MenuTree {
  return buildMenuTree(MAIN_MENU_ID, SHELL_SCREENS);
}
